// Generate reservation + discovery deep links for a restaurant card.
// PointsPilot doesn't book restaurants directly — we hand off to OpenTable,
// Resy, SevenRooms, or Google to actually book. These links pre-fill the
// search query with the restaurant name + city so the user lands one click
// away from the reservation page.

function buildSearchUrl(base, query) {
  return base + encodeURIComponent(query);
}

export function getRestaurantLinks(restaurant) {
  if (!restaurant) return [];
  const name = restaurant.name || '';
  const city = (restaurant.neighborhood || '').split(',').pop()?.trim() || '';
  const query = `${name} ${city}`.trim();

  return [
    {
      label: 'OpenTable',
      url: buildSearchUrl('https://www.opentable.com/s?term=', query),
      hint: 'Reservations for most US/UK/Mexico restaurants',
      icon: '🍽️',
      color: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    },
    {
      label: 'Resy',
      url: buildSearchUrl('https://resy.com/cities/search?query=', query),
      hint: 'Hot reservations especially in major US cities',
      icon: '⭐',
      color: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    },
    {
      label: 'TheFork',
      url: buildSearchUrl('https://www.thefork.com/search?cityName=', query),
      hint: 'European reservations — strongest in France, Spain, Italy, UK',
      icon: '🍴',
      color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    },
    {
      label: 'Google',
      url: buildSearchUrl('https://www.google.com/search?q=', query + ' reservation'),
      hint: 'Restaurant phone + booking link in Google results',
      icon: '🔎',
      color: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    },
    {
      label: 'Maps',
      url: buildSearchUrl('https://www.google.com/maps/search/?api=1&query=', query),
      hint: 'Walking directions + hours from Google Maps',
      icon: '📍',
      color: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
    },
  ];
}

// Booking-window guidance: how far ahead to book each type. Pinned to
// real-world patterns — hot Michelin spots open ~30 days out, normal
// places ~7-14, casual takes walk-ins.
export function getBookingWindow(restaurant) {
  const cat = restaurant?.category || '';
  const range = restaurant?.price_range || '';

  if (cat === 'fine_dining' || range === '$$$$') {
    return {
      window: '30+ days ahead',
      urgent: true,
      note: 'Michelin / tasting menu spots open reservations 30-60 days ahead and fill in minutes. Set a calendar reminder for the morning bookings drop.',
    };
  }
  if (cat === 'local_favorite' || range === '$$$') {
    return {
      window: '14 days ahead',
      urgent: false,
      note: 'Popular local spots fill the prime 7-9 PM window 1-2 weeks ahead. Off-peak (5:30 or 9:30 PM) opens up later.',
    };
  }
  if (cat === 'casual') {
    return {
      window: '2-3 days ahead (or walk-in)',
      urgent: false,
      note: 'Most casual spots take walk-ins or same-week bookings. Bigger party (4+) — book ahead just in case.',
    };
  }
  return {
    window: '1 week ahead',
    urgent: false,
    note: 'A week ahead is a safe target for most restaurants.',
  };
}
